import { createStore } from 'zustand/vanilla';
import type { ArquivoEnviadoDto, ResultadoImportacaoDto, SecaoConfiguracao } from '@nexital/shared';

/** O que a bandeja faz com um arquivo sem processo (ADR-0020). */
export type AcaoArquivo = 'vincular' | 'nova-licitacao' | 'descartar';

export type ApiEnvios = {
  importar(arquivos: File[], processoUuid?: string): Promise<ResultadoImportacaoDto>;
  bandeja(): Promise<ArquivoEnviadoDto[]>;
  acao(arquivoUuid: string, acao: AcaoArquivo, processoUuid?: string): Promise<void>;
};

/**
 * Estado da tela do quadro além dos dados: envio de arquivos e bandeja (ADR-0020), avisos do sistema (mensagens curtas
 * que somem sozinhas) e o convite aberto para aprovação (ADR-0021).
 */
export type EstadoEnvio = {
  /** Diálogo "Enviar arquivos" aberto, com os arquivos já soltos no quadro (ou vazio pelo botão). */
  dialogo: File[] | null;
  /** Arquivos sem processo (sugeridos ou sem identificação). */
  bandeja: ArquivoEnviadoDto[];
  aviso: string | null;
  /** Convite aberto para aprovar (pelo aviso no sino ou pelo link do e-mail). */
  convite: string | null;
  /** Tela de Configurações aberta (ADR-0022), com a seção escolhida. */
  configuracoes: SecaoConfiguracao | 'inicio' | null;
  abrirConfiguracoes(secao: SecaoConfiguracao | 'inicio' | null): void;
  avisar(texto: string): void;
  abrirConvite(uuid: string | null): void;
  abrirDialogo(arquivos?: File[]): void;
  fecharDialogo(): void;
  carregarBandeja(): Promise<void>;
  /** Arquivos soltos sobre um cartão: vão direto para aquele processo. */
  enviarAoProcesso(arquivos: File[], processo: { uuid: string; numero: string }): Promise<void>;
  agir(arquivoUuid: string, acao: AcaoArquivo, processo?: { uuid: string; numero: string }): Promise<void>;
  limparAviso(): void;
};

const MENSAGENS: Record<AcaoArquivo, (numero?: string) => string> = {
  vincular: (numero) => `Arquivo vinculado a ${numero}.`,
  'nova-licitacao': () => 'O arquivo vai virar uma licitação nova no quadro.',
  descartar: () => 'Arquivo descartado.',
};

const erro = (e: unknown) => (e instanceof Error ? e.message : 'Não foi possível concluir.');

/** Envio de arquivos e bandeja "Arquivos sem processo" do quadro atual (ADR-0020). */
export function criarStoreEnvio(api: ApiEnvios) {
  return createStore<EstadoEnvio>()((setState, getState) => ({
    dialogo: null,
    bandeja: [],
    aviso: null,
    convite: null,
    configuracoes: null,
    abrirConfiguracoes: (configuracoes) => setState({ configuracoes }),
    avisar: (aviso) => setState({ aviso }),
    abrirConvite: (convite) => setState({ convite }),
    abrirDialogo: (arquivos = []) => setState({ dialogo: arquivos }),
    fecharDialogo: () => {
      setState({ dialogo: null });
      void getState().carregarBandeja();
    },
    carregarBandeja: async () => {
      const bandeja = await api.bandeja().catch(() => null);
      if (bandeja) setState({ bandeja });
    },
    enviarAoProcesso: async (arquivos, processo) => {
      const plural = arquivos.length === 1 ? 'o arquivo' : `os ${arquivos.length} arquivos`;
      setState({ aviso: `Enviando ${plural} para ${processo.numero}…` });
      try {
        const r = await api.importar(arquivos, processo.uuid);
        const recusados = r.recusados.length ? ` Não aceitos: ${r.recusados.map((x) => `${x.nome} (${x.motivo})`).join(', ')}.` : '';
        setState({ aviso: `Recebido. O Nexital lê e anexa em ${processo.numero}; você recebe um aviso quando terminar.${recusados}` });
      } catch (e) {
        setState({ aviso: erro(e) });
      }
    },
    agir: async (arquivoUuid, acao, processo) => {
      try {
        await api.acao(arquivoUuid, acao, processo?.uuid);
        setState({ aviso: MENSAGENS[acao](processo?.numero), bandeja: getState().bandeja.filter((a) => a.uuid !== arquivoUuid) });
      } catch (e) {
        setState({ aviso: erro(e) });
      }
      await getState().carregarBandeja();
    },
    limparAviso: () => setState({ aviso: null }),
  }));
}

export type StoreEnvio = ReturnType<typeof criarStoreEnvio>;
